import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable } from '../../../../shared/components/index.js';

interface AlertRow {
  readonly alertRuleId: string;
  readonly alertCode: string;
  readonly alertName: string;
  readonly alertType: string;
  readonly severity: string;
  readonly conditionOperator: string;
  readonly thresholdValue: string | null;
  readonly isActive: boolean;
  readonly triggerCount: number;
  readonly status: string;
}

const ALERT_TYPES = ['KPI_BREACH', 'THRESHOLD_VIOLATION', 'PERFORMANCE_DROP', 'SYSTEM_FAILURE', 'COMPLIANCE_ISSUE', 'RISK_ESCALATION'];
const OPERATORS = ['GT', 'GTE', 'LT', 'LTE', 'EQ', 'NEQ', 'BETWEEN', 'OUTSIDE'];
const SEVERITIES = ['INFO', 'WARNING', 'CRITICAL'];
const STATUSES = ['ACTIVE', 'SUSPENDED', 'ARCHIVED'];

/**
 * M37 — Alert Management (§28 / §16 Alert & Notification Engine). Register of
 * alert rules (T499) bound to a KPI or metric with a comparison operator,
 * threshold(s), severity and a cooldown window (LAW-6). Authors create rules
 * (ALERT_MANAGE) and evaluate a candidate value against a rule — the engine
 * applies the operator, honours the cooldown and raises ALERT_TRIGGERED when
 * the condition is met (BR-04). Filterable by type and status. Tenant isolation
 * is enforced server-side (LAW 4). A 403 surfaces a clear permission message.
 */
export function AlertManagementScreen(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<AlertRow[]>([]);
  const [total, setTotal] = useState(0);
  const [alertType, setAlertType] = useState('');
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  // Create form
  const [alertName, setAlertName] = useState('');
  const [newType, setNewType] = useState('KPI_BREACH');
  const [kpiId, setKpiId] = useState('');
  const [operator, setOperator] = useState('GT');
  const [thresholdValue, setThresholdValue] = useState('');
  const [severity, setSeverity] = useState('WARNING');
  const [newStatus, setNewStatus] = useState('ACTIVE');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // Evaluate panel
  const [evalAlert, setEvalAlert] = useState<AlertRow | null>(null);
  const [evalValue, setEvalValue] = useState('');
  const [evalBusy, setEvalBusy] = useState(false);
  const [evalError, setEvalError] = useState<string | null>(null);
  const [evalResult, setEvalResult] = useState<{ readonly triggered: boolean; readonly reason: string } | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (alertType) params.set('alertType', alertType);
    if (status) params.set('status', status);
    if (q) params.set('q', q);
    const qs = params.toString();
    apiRequest<{ data: AlertRow[]; total: number }>(`/v1/bi/alerts${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setTotal(r.total); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, alertType, status, q, reload]);

  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!alertName.trim()) { setActionError(t('m37.alerts.nameRequired')); return; }
    setBusy(true);
    try {
      const r = await apiRequest<{ data: AlertRow }>(`/v1/bi/alerts`, {
        method: 'POST',
        body: {
          alertName: alertName.trim(),
          alertType: newType,
          kpiId: kpiId.trim() || undefined,
          conditionOperator: operator,
          thresholdValue: thresholdValue.trim() ? Number(thresholdValue) : undefined,
          severity,
          status: newStatus,
        },
        token,
      });
      setNotice(t('m37.alerts.created', { code: r.data.alertCode, name: r.data.alertName }));
      setAlertName('');
      setKpiId('');
      setThresholdValue('');
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m37.alerts.notAuthCreate'));
      } else {
        setActionError((err as Error).message);
      }
    } finally {
      setBusy(false);
    }
  };

  const submitEvaluate = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setEvalError(null);
    setEvalResult(null);
    if (!evalAlert) return;
    if (!evalValue.trim()) { setEvalError(t('m37.alerts.valueRequired')); return; }
    setEvalBusy(true);
    try {
      const r = await apiRequest<{ data: { triggered: boolean; reason: string } }>(`/v1/bi/alerts/${evalAlert.alertRuleId}/evaluate`, {
        method: 'POST',
        body: { value: Number(evalValue) },
        token,
      });
      setEvalResult(r.data);
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setEvalError(t('m37.alerts.notAuthEvaluate'));
      } else {
        setEvalError((err as Error).message);
      }
    } finally {
      setEvalBusy(false);
    }
  };

  return (
    <main aria-label="alert-management">
      <h1>{t('m37.alerts.title')}</h1>

      <section aria-label="create-alert">
        <h2>{t('m37.alerts.createTitle')}</h2>
        <form aria-label="alert-form" onSubmit={(e) => void submit(e)}>
          <label>{t('m37.common.name')}<input aria-label="field-name" value={alertName} onChange={(e) => setAlertName(e.target.value)} /></label>
          <label>{t('m37.common.type')}
            <select aria-label="field-type" value={newType} onChange={(e) => setNewType(e.target.value)}>
              {ALERT_TYPES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <label>{t('m37.alerts.kpiId')}<input aria-label="field-kpi" value={kpiId} onChange={(e) => setKpiId(e.target.value)} placeholder={t('m37.alerts.kpiPlaceholder')} /></label>
          <label>{t('m37.alerts.operator')}
            <select aria-label="field-operator" value={operator} onChange={(e) => setOperator(e.target.value)}>
              {OPERATORS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <label>{t('m37.alerts.threshold')}<input aria-label="field-threshold" type="number" value={thresholdValue} onChange={(e) => setThresholdValue(e.target.value)} /></label>
          <label>{t('m37.common.severity')}
            <select aria-label="field-severity" value={severity} onChange={(e) => setSeverity(e.target.value)}>
              {SEVERITIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <label>{t('m37.common.status')}
            <select aria-label="field-status" value={newStatus} onChange={(e) => setNewStatus(e.target.value)}>
              {STATUSES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <Button variant="primary" type="submit" aria-label="submit-alert" disabled={busy}>{busy ? t('m37.common.saving') : t('m37.alerts.createBtn')}</Button>
        </form>
      </section>

      {actionError && <p role="alert" aria-label="action-error">{actionError}</p>}
      {notice && <p role="status" aria-label="notice">{notice}</p>}

      <section aria-label="alert-list">
        <h2>{t('m37.alerts.listTitle')}</h2>
        <div className="filters">
          <label>{t('m37.common.type')}
            <select aria-label="filter-type" value={alertType} onChange={(e) => setAlertType(e.target.value)}>
              {['', ...ALERT_TYPES].map((s) => <option key={s || 'all'} value={s}>{s || t('m37.common.all')}</option>)}
            </select>
          </label>
          <label>{t('m37.common.status')}
            <select aria-label="filter-status" value={status} onChange={(e) => setStatus(e.target.value)}>
              {['', ...STATUSES].map((s) => <option key={s || 'all'} value={s}>{s || t('m37.common.all')}</option>)}
            </select>
          </label>
          <label>{t('m37.common.search')}<input aria-label="filter-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('m37.common.searchPlaceholder')} /></label>
        </div>
        {error && <p role="alert">{error}</p>}
        {loading ? <p>{t('common.loading')}</p> : (
          <>
            <p aria-label="alert-count">{t('m37.alerts.count', { count: total })}</p>
            <DataTable
              ariaLabel="alert-table"
              rows={rows}
              rowKey={(r) => r.alertRuleId}
              columns={[
                { key: 'alertCode', header: t('m37.common.code'), render: (r) => r.alertCode },
                { key: 'alertName', header: t('m37.common.name'), render: (r) => r.alertName },
                { key: 'alertType', header: t('m37.common.type'), render: (r) => r.alertType },
                { key: 'severity', header: t('m37.common.severity'), render: (r) => r.severity },
                { key: 'conditionOperator', header: t('m37.alerts.operator'), render: (r) => r.conditionOperator },
                { key: 'thresholdValue', header: t('m37.alerts.threshold'), render: (r) => r.thresholdValue ?? '—' },
                { key: 'triggerCount', header: t('m37.alerts.triggers'), render: (r) => r.triggerCount },
                { key: 'status', header: t('m37.common.status'), render: (r) => r.status },
                { key: 'actions', header: t('m37.common.actions'), render: (r) => <Button variant="secondary" type="button" aria-label={`evaluate-${r.alertRuleId}`} onClick={() => { setEvalAlert(r); setEvalResult(null); setEvalError(null); }}>{t('m37.alerts.evaluate')}</Button> },
              ]}
            />
          </>
        )}
      </section>

      {evalAlert && (
        <section aria-label="evaluate-alert">
          <h2>{t('m37.alerts.evaluateTitle', { name: evalAlert.alertName })}</h2>
          <form aria-label="evaluate-form" onSubmit={(e) => void submitEvaluate(e)}>
            <label>{t('m37.common.value')}<input aria-label="evaluate-value" type="number" value={evalValue} onChange={(e) => setEvalValue(e.target.value)} /></label>
            <Button variant="primary" type="submit" aria-label="submit-evaluate" disabled={evalBusy}>{evalBusy ? t('m37.alerts.evaluating') : t('m37.alerts.evaluate')}</Button>
            <Button variant="danger" type="button" aria-label="cancel-evaluate" onClick={() => setEvalAlert(null)}>{t('m37.common.close')}</Button>
          </form>
          {evalError && <p role="alert" aria-label="evaluate-error">{evalError}</p>}
          {evalResult && <p role="status" aria-label="evaluate-result">{evalResult.triggered ? t('m37.alerts.triggered') : t('m37.alerts.notTriggered')} — {evalResult.reason}</p>}
        </section>
      )}
    </main>
  );
}
